/**
 * Centralized Notification Service
 * Provides consistent, user-friendly notifications across the entire application
 * 
 * UX Best Practices:
 * - Standardized positioning (top-right for all)
 * - Appropriate durations based on message length and type
 * - Consistent icons and colors
 * - Accessible and readable
 * 
 * @example
 * import NotificationService from '@/services/notification.service'
 * 
 * // Show success message
 * NotificationService.success('Record saved successfully!')
 * 
 * // Show error with longer duration
 * NotificationService.error('An error occurred', 'Please check your input and try again')
 * 
 * // Custom notification
 * NotificationService.show({
 *   title: 'Custom',
 *   message: 'Custom message',
 *   type: 'info'
 * })
 */

class NotificationService {
  constructor() {
    this.vueInstance = null
    
    // Default configuration - following UX best practices
    this.defaultConfig = {
      position: 'bottom-right', // Consistent position (matches client expectations)
      iconPack: 'feather',
      
      // Durations based on UX research
      durations: {
        short: 3000,    // 3 seconds - quick confirmations
        medium: 5000,   // 5 seconds - standard messages
        long: 7000,     // 7 seconds - important messages
        critical: 10000 // 10 seconds - errors requiring attention
      },
      
      // Colors for different message types
      colors: {
        success: 'success',
        error: 'danger',
        warning: 'warning',
        info: 'primary',
      },
      
      // Icons for different message types
      icons: {
        success: 'icon-check-circle',
        error: 'icon-alert-circle',
        warning: 'icon-alert-triangle',
        info: 'icon-info',
        permission: 'icon-shield-off',
        save: 'icon-save',
        delete: 'icon-trash-2',
        update: 'icon-edit',
        create: 'icon-plus-circle',
      }
    }
  }

  /**
   * Initialize the service with Vue instance
   * Called from main.js after Vue is mounted
   */
  init(vueInstance) {
    this.vueInstance = vueInstance
  }

  /**
   * Calculate appropriate duration based on message length
   * Longer messages get more time to read
   */
  _calculateDuration(text, minDuration = 3000) {
    if (!text) return minDuration
    
    // Average reading speed: 200 words per minute = ~3.3 words per second
    // Average word length: 5 characters
    const words = text.length / 5
    const readingTime = (words / 3.3) * 1000 // Convert to milliseconds
    
    // Add buffer time for user to process
    const bufferTime = 2000
    const calculatedDuration = Math.ceil(readingTime + bufferTime)
    
    // Ensure minimum and maximum bounds
    return Math.max(minDuration, Math.min(calculatedDuration, 15000))
  }

  /**
   * Core notification method
   * All other methods use this internally
   */
  show({
    title,
    message = '',
    type = 'info',
    duration = null,
    position = null,
    icon = null,
    color = null,
    onClick = null,
    sticky = false,
    buttonText = null,
    onButtonClick = null
  }) {
    if (!this.vueInstance || !this.vueInstance.$vs) {
      console.error('NotificationService not initialized. Call init() with Vue instance first.')
      return
    }

    // Calculate duration if not provided
    const finalDuration = duration || 
      (sticky ? 0 : this._calculateDuration(title + ' ' + message, this.defaultConfig.durations.medium))

    // Build notification config
    const config = {
      title,
      text: message,
      color: color || this.defaultConfig.colors[type] || this.defaultConfig.colors.info,
      position: position || this.defaultConfig.position,
      iconPack: this.defaultConfig.iconPack,
      icon: icon || this.defaultConfig.icons[type] || this.defaultConfig.icons.info,
      time: finalDuration
    }

    // Add button if provided (Vuesax limitation: can't add buttons to notifications)
    // For action buttons, recommend using $vs.dialog instead
    if (buttonText && onButtonClick) {
      config.text = `${message} - Click to ${buttonText}`
      config.click = onButtonClick
    } else if (onClick) {
      config.click = onClick
    }

    this.vueInstance.$vs.notify(config)
  }

  /**
   * Success notification
   * @param {string} title - Main message
   * @param {string} message - Optional detailed message
   * @param {object} options - Additional options
   */
  success(title, message = '', options = {}) {
    this.show({
      title,
      message,
      type: 'success',
      duration: options.duration || this.defaultConfig.durations.short,
      ...options
    })
  }

  /**
   * Error notification
   * @param {string} title - Error title
   * @param {string} message - Error details
   * @param {object} options - Additional options
   */
  error(title, message = '', options = {}) {
    this.show({
      title,
      message,
      type: 'error',
      duration: options.duration || this.defaultConfig.durations.critical,
      ...options
    })
  }

  /**
   * Warning notification
   * @param {string} title - Warning title
   * @param {string} message - Warning details
   * @param {object} options - Additional options
   */
  warning(title, message = '', options = {}) {
    this.show({
      title,
      message,
      type: 'warning',
      duration: options.duration || this.defaultConfig.durations.long,
      ...options
    })
  }

  /**
   * Info notification
   * @param {string} title - Info title
   * @param {string} message - Info details
   * @param {object} options - Additional options
   */
  info(title, message = '', options = {}) {
    this.show({
      title,
      message,
      type: 'info',
      duration: options.duration || this.defaultConfig.durations.medium,
      ...options
    })
  }

  /**
   * Permission denied notification
   * Specifically for 403 errors
   */
  permissionDenied(message = 'Insufficient permission to perform this action') {
    this.show({
      title: 'Access Denied',
      message,
      type: 'error',
      icon: this.defaultConfig.icons.permission,
      duration: this.defaultConfig.durations.long
    })
  }

  /**
   * CRUD operation notifications
   * Convenient methods for common operations
   */
  created(resourceName = 'Record') {
    this.success(
      `${resourceName} Created`,
      'Information has been successfully saved',
      { icon: this.defaultConfig.icons.create }
    )
  }

  updated(resourceName = 'Record') {
    this.success(
      `${resourceName} Updated`,
      'Information has been successfully updated',
      { icon: this.defaultConfig.icons.update }
    )
  }

  deleted(resourceName = 'Record') {
    this.success(
      `${resourceName} Deleted`,
      'Record has been successfully removed',
      { icon: this.defaultConfig.icons.delete }
    )
  }

  saved(resourceName = 'Record') {
    this.success(
      `${resourceName} Saved`,
      'Changes have been saved successfully',
      { icon: this.defaultConfig.icons.save }
    )
  }

  /**
   * Network error notification
   */
  networkError() {
    this.error(
      'Network Error',
      'Unable to connect to the server. Please check your internet connection.',
      { duration: this.defaultConfig.durations.critical }
    )
  }

  /**
   * Validation error notification
   */
  validationError(message = 'Please check your input and try again') {
    this.warning(
      'Validation Error',
      message,
      { duration: this.defaultConfig.durations.long }
    )
  }

  /**
   * Loading notification (sticky)
   * Returns ID that can be used to close it
   */
  loading(title = 'Processing...', message = 'Please wait') {
    this.show({
      title,
      message,
      type: 'info',
      sticky: true,
      icon: 'icon-loader'
    })
  }

  /**
   * Confirmation with action
   * Shows notification with clickable action
   */
  withAction(title, message, actionText, actionCallback) {
    // Vuesax doesn't support action buttons in notifications
    // But we can use click handler as workaround
    this.info(
      title,
      `${message} (Click to ${actionText})`,
      {
        onClick: actionCallback,
        duration: this.defaultConfig.durations.long
      }
    )
  }

  /**
   * Show confirmation dialog (uses modal, not toast)
   * Better UX for critical actions
   */
  confirm({
    title = 'Confirm Action',
    message,
    acceptText = 'Confirm',
    cancelText = 'Cancel',
    type = 'confirm', // 'confirm' | 'alert'
    color = 'primary', // 'primary' | 'danger' | 'warning'
    onAccept,
    onCancel
  }) {
    if (!this.vueInstance || !this.vueInstance.$vs) {
      console.error('NotificationService not initialized.')
      return
    }

    this.vueInstance.$vs.dialog({
      type,
      color,
      title,
      text: message,
      acceptText,
      cancelText,
      accept: onAccept,
      cancel: onCancel
    })
  }

  /**
   * Show delete confirmation
   * Convenience method for common delete pattern
   */
  confirmDelete(itemName, onConfirm) {
    this.confirm({
      title: `Delete ${itemName}?`,
      message: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      acceptText: 'Yes, Delete',
      cancelText: 'Keep It',
      type: 'confirm',
      color: 'danger',
      onAccept: onConfirm
    })
  }

  /**
   * Show unsaved changes warning
   * Common pattern for form navigation
   */
  confirmUnsavedChanges(onDiscard, onCancel) {
    this.confirm({
      title: 'Unsaved Changes',
      message: 'You have unsaved changes that will be lost if you continue. Do you want to discard your changes?',
      acceptText: 'Discard Changes',
      cancelText: 'Keep Editing',
      type: 'confirm',
      color: 'warning',
      onAccept: onDiscard,
      onCancel: onCancel
    })
  }
}

// Create singleton instance
const notificationService = new NotificationService()

// Export as default
export default notificationService

// Also export the class for testing/extending
export { NotificationService }

